import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useState } from 'react';

interface DiscountRulesSelectorProps {
  selectedRules: string | null;
  setSelectedRules: (rules: string | null) => void;
}

export function DiscountRulesSelector({
  selectedRules,
  setSelectedRules,
}: DiscountRulesSelectorProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingRule, setPendingRule] = useState<string | null>(null);
  const options = [
    {
      id: 'all-months',
      label: 'Apply to all subscription months',
      description: 'Discount will be applied to every monthly payment',
    },
    {
      id: 'first-month',
      label: 'Apply to first month only',
      description: 'Discount will only be applied to the first payment',
    },
  ] as const;

  const handleRuleChange = (newRule: string) => {
    if (newRule !== selectedRules) {
      setPendingRule(newRule);
      setShowConfirmModal(true);
    }
  };

  return (
    <>
      <div className='mt-8'>
        <button
          onClick={() => {
            setIsOpen(!isOpen);
          }}
          className='mb-4 flex w-full items-center justify-between rounded-lg bg-white px-4 py-3 text-left shadow'
        >
          <h3 className='text-lg font-medium text-gray-900'>
            Discount Rules{' '}
            <span className='text-red-500'>
              (This is same for both multi-class and multi-student)
            </span>
          </h3>
          {isOpen ? (
            <ChevronUpIcon className='size-5 text-gray-500' />
          ) : (
            <ChevronDownIcon className='size-5 text-gray-500' />
          )}
        </button>

        <div
          className={`overflow-hidden transition-all duration-200 ease-in-out ${
            isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div
            className={`rounded-lg border border-gray-200 bg-white shadow ${
              isOpen ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className='divide-y divide-gray-200'>
              {options.map((option) => (
                <div
                  key={option.id}
                  className='relative flex cursor-pointer items-start p-4'
                  onClick={() => {
                    handleRuleChange(option.id);
                  }}
                >
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-center'>
                      <input
                        type='radio'
                        checked={selectedRules === option.id}
                        onChange={() => {
                          handleRuleChange(option.id);
                        }}
                        className='size-4 border-gray-300 text-yellow-400 focus:ring-yellow-400'
                      />
                      <label className='ml-3 text-sm font-medium text-gray-700'>
                        {option.label}
                      </label>
                    </div>
                    <p className='ml-7 mt-1 text-sm text-gray-500'>
                      {option.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <div className='fixed inset-0 z-[9999] overflow-y-auto'>
          <div className='flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0'>
            <div
              className='fixed inset-0 bg-gray-500/75 transition-opacity'
              onClick={() => {
                setShowConfirmModal(false);
              }}
            />

            <div className='relative inline-block overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle'>
              <div>
                <div className='mt-3 text-center sm:mt-5'>
                  <h3 className='text-lg font-medium leading-6 text-gray-900'>
                    Change Discount Rule?
                  </h3>
                  <div className='mt-2'>
                    <p className='text-sm text-gray-500'>
                      Are you sure you want to change the discount rule? This
                      will affect how discounts are applied to subscriptions.
                    </p>
                  </div>
                </div>
              </div>
              <div className='mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3'>
                <button
                  type='button'
                  className='inline-flex w-full justify-center rounded-md border border-transparent bg-yellow-400 px-4 py-2 text-base font-medium text-black shadow-sm hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 sm:col-start-2 sm:text-sm'
                  onClick={() => {
                    if (pendingRule) {
                      setSelectedRules(pendingRule);
                    }
                    setShowConfirmModal(false);
                    setPendingRule(null);
                  }}
                >
                  Confirm
                </button>
                <button
                  type='button'
                  className='mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm'
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPendingRule(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
